(() => {
  class KoffylandProductAction {
    constructor(productInfo) {
      this.productInfo = productInfo;
      this.sectionId = productInfo.dataset.section;
      this.form = productInfo.querySelector('[data-kl-product-action-form]');
      this.variantInput = this.form?.querySelector('[data-kl-product-variant-input]');
      this.addButton = this.form?.querySelector('[data-kl-add-to-cart-button]');
      this.purchaseControls = this.form?.querySelector('[data-kl-purchase-controls]');
      this.purchaseQuantity = this.form?.querySelector('[data-kl-purchase-quantity]');
      this.cartControls = this.form?.querySelector('[data-kl-cart-controls]');
      this.cartQuantityDisplay = this.form?.querySelector('[data-kl-cart-quantity]');
      this.priceWrapper = this.form?.querySelector('[data-kl-product-action-price-wrapper]');

      this.stickyBar = productInfo.querySelector('[data-kl-sticky-buy]');
      this.stickyPurchaseControls = this.stickyBar?.querySelector('[data-kl-sticky-purchase-controls]');
      this.stickyPurchaseQuantity = this.stickyBar?.querySelector('[data-kl-sticky-purchase-quantity]');
      this.stickySubmit = this.stickyBar?.querySelector('[data-kl-sticky-submit]');
      this.stickySubmitText = this.stickyBar?.querySelector('[data-kl-sticky-submit-text]');
      this.stickyCartControls = this.stickyBar?.querySelector('[data-kl-sticky-cart-controls]');
      this.stickyCartQuantityDisplay = this.stickyBar?.querySelector('[data-kl-sticky-cart-quantity]');
      this.stickyVariantText = this.stickyBar?.querySelector('[data-kl-sticky-variant]');
      this.stickyPrice = this.stickyBar?.querySelector('[data-kl-sticky-price]');
      this.stickyThumbnail = this.stickyBar?.querySelector('[data-kl-sticky-thumbnail]');

      this.cartDrawer = document.querySelector('cart-drawer');
      this.addButtonLabel = this.addButton?.textContent.trim() || window.variantStrings?.addToCart || '';
      this.variantStatus = this.form?.dataset.klVariantAvailable === 'true' ? 'available' : 'sold_out';
      this.cartQuantity = 0;
      this.isLoading = false;
      this.hasSeenForm = false;
      this.raf = null;
    }

    init() {
      if (!this.form || !this.variantInput || !this.addButton) return;

      if (this.stickyBar) this.stickyBar.hidden = false;

      this.bindEvents();
      this.render();
      this.requestVisibilityUpdate();
      this.refreshCartState();
    }

    bindEvents() {
      this.form.addEventListener('submit', (event) => this.handleAddToCart(event));

      this.form.querySelectorAll('[data-kl-purchase-adjust]').forEach((button) => {
        button.addEventListener('click', () => this.adjustPurchaseQuantity(button.dataset.klPurchaseAdjust));
      });

      this.purchaseQuantity?.addEventListener('input', () => this.syncPurchaseQuantity(this.purchaseQuantity.value, 'main'));
      this.purchaseQuantity?.addEventListener('change', () =>
        this.syncPurchaseQuantity(this.purchaseQuantity.value, 'main', { clamp: true })
      );

      this.form.querySelectorAll('[data-kl-cart-adjust]').forEach((button) => {
        button.addEventListener('click', () => this.adjustCartQuantity(button.dataset.klCartAdjust));
      });

      this.stickyBar?.querySelectorAll('[data-kl-sticky-purchase-adjust]').forEach((button) => {
        button.addEventListener('click', () => this.adjustPurchaseQuantity(button.dataset.klStickyPurchaseAdjust));
      });

      this.stickyPurchaseQuantity?.addEventListener('input', () =>
        this.syncPurchaseQuantity(this.stickyPurchaseQuantity.value, 'sticky')
      );
      this.stickyPurchaseQuantity?.addEventListener('change', () =>
        this.syncPurchaseQuantity(this.stickyPurchaseQuantity.value, 'sticky', { clamp: true })
      );

      this.stickyBar?.querySelectorAll('[data-kl-sticky-cart-adjust]').forEach((button) => {
        button.addEventListener('click', () => this.adjustCartQuantity(button.dataset.klStickyCartAdjust));
      });

      this.stickySubmit?.addEventListener('click', () => this.addCurrentVariant());

      window.addEventListener('scroll', () => this.requestVisibilityUpdate(), { passive: true });
      window.addEventListener('resize', () => this.requestVisibilityUpdate());

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.handleVariantChange(event));
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.handleExternalCartUpdate(event));
      }
    }

    handleAddToCart(event) {
      event.preventDefault();
      this.addCurrentVariant();
    }

    currentVariantId() {
      const value = Number(this.variantInput?.value);
      return Number.isFinite(value) && value > 0 ? value : null;
    }

    currentAddLabel() {
      if (!this.currentVariantId()) return window.variantStrings?.unavailable || this.addButtonLabel;
      if (this.variantStatus === 'sold_out') return window.variantStrings?.soldOut || this.addButtonLabel;
      return this.addButtonLabel;
    }

    quantityBounds() {
      const input = this.purchaseQuantity || this.stickyPurchaseQuantity;
      return {
        min: Number(input?.min) || 1,
        max: input?.max ? Number(input.max) : null,
        step: Number(input?.step) || 1,
      };
    }

    normalizeQuantity(value, fallback) {
      const { min, max } = this.quantityBounds();
      const parsed = Number(value);
      const safeFallback = Number.isFinite(fallback) ? fallback : min;
      const normalized = Number.isFinite(parsed) ? parsed : safeFallback;
      const clamped = Math.max(min, normalized);
      return max ? Math.min(clamped, max) : clamped;
    }

    purchaseQuantityValue() {
      const { min } = this.quantityBounds();
      const value = this.purchaseQuantity?.value || this.stickyPurchaseQuantity?.value || min;
      return this.normalizeQuantity(value, min);
    }

    syncPurchaseQuantity(value, source, options = {}) {
      const nextValue = options.clamp
        ? this.normalizeQuantity(value, this.purchaseQuantityValue())
        : value;
      const stringValue = String(nextValue);

      if (source !== 'main' && this.purchaseQuantity) this.purchaseQuantity.value = stringValue;
      if (source !== 'sticky' && this.stickyPurchaseQuantity) this.stickyPurchaseQuantity.value = stringValue;

      if (source === 'main' && this.purchaseQuantity) this.purchaseQuantity.value = stringValue;
      if (source === 'sticky' && this.stickyPurchaseQuantity) this.stickyPurchaseQuantity.value = stringValue;
    }

    adjustPurchaseQuantity(direction) {
      const { step, min } = this.quantityBounds();
      const current = this.purchaseQuantityValue();
      const next = direction === 'minus' ? current - step : current + step;
      this.syncPurchaseQuantity(this.normalizeQuantity(next, min), null, { clamp: true });
    }

    setLoading(isLoading) {
      this.isLoading = isLoading;
      this.form.setAttribute('aria-busy', String(isLoading));
      this.render();
    }

    render() {
      const hasCartQuantity = this.cartQuantity > 0;
      const addLabel = this.currentAddLabel();
      const addDisabled = this.isLoading || this.variantStatus !== 'available' || !this.currentVariantId();

      if (this.purchaseControls) this.purchaseControls.hidden = hasCartQuantity;
      this.addButton.hidden = hasCartQuantity;
      if (this.cartControls) this.cartControls.hidden = !hasCartQuantity;

      if (this.stickyPurchaseControls) this.stickyPurchaseControls.hidden = hasCartQuantity;
      if (this.stickySubmit) this.stickySubmit.hidden = hasCartQuantity;
      if (this.stickyCartControls) this.stickyCartControls.hidden = !hasCartQuantity;

      this.addButton.disabled = addDisabled;
      this.addButton.textContent = addLabel;

      if (this.stickySubmit) {
        this.stickySubmit.disabled = addDisabled;
        if (this.stickySubmitText) this.stickySubmitText.textContent = addLabel;
      }

      const quantityString = String(this.cartQuantity);
      if (this.cartQuantityDisplay) this.cartQuantityDisplay.textContent = quantityString;
      if (this.stickyCartQuantityDisplay) this.stickyCartQuantityDisplay.textContent = quantityString;

      const canDecrease = !this.isLoading && this.cartQuantity > 0;
      const canIncrease = !this.isLoading && this.variantStatus === 'available';

      this.form.querySelectorAll('[data-kl-cart-adjust="minus"]').forEach((button) => {
        button.disabled = !canDecrease;
      });
      this.form.querySelectorAll('[data-kl-cart-adjust="plus"]').forEach((button) => {
        button.disabled = !canIncrease;
      });

      this.stickyBar?.querySelectorAll('[data-kl-sticky-cart-adjust="minus"]').forEach((button) => {
        button.disabled = !canDecrease;
      });
      this.stickyBar?.querySelectorAll('[data-kl-sticky-cart-adjust="plus"]').forEach((button) => {
        button.disabled = !canIncrease;
      });
    }

    updateHeaderCount(count) {
      document.querySelectorAll('[data-kl-cart-count]').forEach((element) => {
        element.textContent = String(count || 0);
      });
    }

    updateDrawerState(cart) {
      if (!this.cartDrawer || !cart) return;
      this.cartDrawer.classList.toggle('is-empty', cart.item_count === 0);
      this.cartDrawer.querySelector('cart-drawer-items')?.classList.toggle('is-empty', cart.item_count === 0);
    }

    findVariantQuantity(cart) {
      const variantId = this.currentVariantId();
      if (!cart || !variantId) return 0;
      return cart.items.reduce((total, item) => {
        return item.variant_id === variantId ? total + item.quantity : total;
      }, 0);
    }

    applyCart(cart) {
      this.cartQuantity = this.findVariantQuantity(cart);
      if (typeof cart?.item_count === 'number') this.updateHeaderCount(cart.item_count);
      this.updateDrawerState(cart);
      this.render();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    }

    fetchCartState() {
      return fetch(`${routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      })
        .then((response) => response.json())
        .catch((error) => {
          console.error(error);
          return null;
        });
    }

    refreshCartState() {
      return this.fetchCartState().then((cart) => {
        if (cart) this.applyCart(cart);
        return cart;
      });
    }

    notifyCartUpdate(cart) {
      if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'kl-product-action',
        cartData: cart,
        variantId: this.currentVariantId(),
      });
    }

    addCurrentVariant() {
      const variantId = this.currentVariantId();
      if (!variantId || this.isLoading || this.variantStatus !== 'available') return;

      this.setLoading(true);

      fetch(routes.cart_add_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          id: variantId,
          quantity: this.purchaseQuantityValue(),
        }),
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            throw new Error(response.description || response.message || window.cartStrings?.error || 'Cart error');
          }

          return this.fetchCartState();
        })
        .then((cart) => {
          if (!cart) return;
          this.applyCart(cart);
          this.notifyCartUpdate(cart);
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.setLoading(false);
        });
    }

    adjustCartQuantity(direction) {
      if (this.isLoading || !this.currentVariantId()) return;

      const nextQuantity = direction === 'minus' ? this.cartQuantity - 1 : this.cartQuantity + 1;
      const variantId = this.currentVariantId();

      this.setLoading(true);

      fetch(routes.cart_update_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          updates: {
            [variantId]: Math.max(0, nextQuantity),
          },
        }),
      })
        .then((response) => response.json())
        .then((cart) => {
          this.applyCart(cart);
          this.notifyCartUpdate(cart);
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.setLoading(false);
        });
    }

    requestVisibilityUpdate() {
      if (!this.stickyBar || this.raf) return;
      this.raf = window.requestAnimationFrame(() => {
        this.raf = null;
        this.updateStickyVisibility();
      });
    }

    updateStickyVisibility() {
      if (!this.stickyBar || !this.form) return;

      const rect = this.form.getBoundingClientRect();
      const isVisible = rect.bottom > 0 && rect.top < window.innerHeight;
      if (isVisible) this.hasSeenForm = true;

      const shouldShow = this.hasSeenForm && !isVisible && rect.bottom <= 0;
      this.stickyBar.classList.toggle('is-visible', shouldShow);
      document.body.classList.toggle('kl-sticky-buy-visible', shouldShow);
    }

    syncQuantityAttributes(sourceInput) {
      if (!sourceInput) return;

      [this.purchaseQuantity, this.stickyPurchaseQuantity].forEach((input) => {
        if (!input) return;

        input.min = sourceInput.min || '1';
        input.step = sourceInput.step || '1';

        if (sourceInput.max) {
          input.max = sourceInput.max;
        } else {
          input.removeAttribute('max');
        }
      });

      this.syncPurchaseQuantity(sourceInput.value || sourceInput.min || '1', null, { clamp: true });
    }

    syncStickyMeta(sourceBar) {
      if (!sourceBar) return;

      const sourcePrice = sourceBar.querySelector('[data-kl-sticky-price]');
      const sourceVariant = sourceBar.querySelector('[data-kl-sticky-variant]');
      const sourceThumbnail = sourceBar.querySelector('[data-kl-sticky-thumbnail]');

      if (sourcePrice && this.stickyPrice) this.stickyPrice.textContent = sourcePrice.textContent;
      if (this.stickyVariantText) this.stickyVariantText.textContent = sourceVariant?.textContent || '';

      if (sourceThumbnail && this.stickyThumbnail) {
        this.stickyThumbnail.src = sourceThumbnail.src;
        this.stickyThumbnail.srcset = sourceThumbnail.srcset;
        this.stickyThumbnail.alt = sourceThumbnail.alt;
      }
    }

    handleVariantChange(event) {
      if (event?.data?.sectionId && event.data.sectionId !== this.sectionId) return;

      const { html, variant } = event?.data || {};
      const sourceForm = html?.querySelector?.('[data-kl-product-action-form]');
      const sourceBar = html?.querySelector?.('[data-kl-sticky-buy]');

      if (!variant) {
        this.variantInput.value = '';
        this.variantStatus = 'unavailable';
        this.cartQuantity = 0;
        this.render();
        return;
      }

      this.variantInput.value = String(variant.id);
      this.variantStatus = variant.available ? 'available' : 'sold_out';

      if (sourceForm) {
        const sourcePriceWrapper = sourceForm.querySelector('[data-kl-product-action-price-wrapper]');
        if (sourcePriceWrapper && this.priceWrapper) this.priceWrapper.innerHTML = sourcePriceWrapper.innerHTML;
        this.syncQuantityAttributes(sourceForm.querySelector('[data-kl-purchase-quantity]'));
      }

      this.syncStickyMeta(sourceBar);
      this.cartQuantity = 0;
      this.render();
      this.refreshCartState();
    }

    handleExternalCartUpdate(event) {
      if (event?.source === 'kl-product-action') return;

      if (event?.cartData) {
        this.applyCart(event.cartData);
        return;
      }

      this.refreshCartState();
    }
  }

  const init = () => {
    document.querySelectorAll("product-info[id^='MainProduct-']").forEach((productInfo) => {
      if (productInfo.dataset.klProductActionInitialized === 'true') return;
      productInfo.dataset.klProductActionInitialized = 'true';
      new KoffylandProductAction(productInfo).init();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
